/*
Onboarding store — persists two booleans in the IDB `meta` store, mirroring
Android OnboardingStore.kt:

- onboardingDone:  the welcome screen has been completed at least once.
                   Drives the router's startDestination decision on cold start.
- splashIntroSeen: the 10-tip splash intro sequence has run at least once.
                   Used by SplashTipsController (not yet ported in the PWA,
                   but the slot is here so the contract stays aligned).

Storage shape: each flag is one row in the `meta` object store, keyed by
its name. Reads return false on missing rows, so a brand-new IDB looks
exactly like a never-onboarded user.
*/

import { kStores, withTx } from './db'

const kKeyOnboardingDone: string = 'onboardingDone'
const kKeySplashIntroSeen: string = 'splashIntroSeen'

interface MetaRow
{
	key: string
	value: unknown
}

/*
Read a boolean meta flag. Missing rows → false.
*/
async function readBool(inKey: string): Promise<boolean>
{
	return withTx([kStores.meta], 'readonly', (inTx) => {
		return new Promise<boolean>((inResolve, inReject) => {
			const vReq = inTx.objectStore(kStores.meta).get(inKey)
			vReq.onsuccess = () => {
				const vRow = vReq.result as MetaRow | undefined  // undefined when absent
				inResolve(vRow?.value === true)
			}
			vReq.onerror = () => inReject(vReq.error)
		})
	})
}

/*
Write a boolean meta flag. Idempotent — writes use `put`, so flipping a
flag to the value it already holds is a no-op at the row level.
*/
async function writeBool(inKey: string, inValue: boolean): Promise<void>
{
	return withTx([kStores.meta], 'readwrite', (inTx) => {
		return new Promise<void>((inResolve, inReject) => {
			const vReq = inTx.objectStore(kStores.meta).put({ key: inKey, value: inValue })
			vReq.onsuccess = () => inResolve()
			vReq.onerror = () => inReject(vReq.error)
		})
	})
}

export async function isOnboardingDone(): Promise<boolean>
{
	return readBool(kKeyOnboardingDone)
}

export async function markOnboardingDone(): Promise<void>
{
	await writeBool(kKeyOnboardingDone, true)
}

export async function isSplashIntroSeen(): Promise<boolean>
{
	return readBool(kKeySplashIntroSeen)
}

export async function markSplashIntroSeen(): Promise<void>
{
	await writeBool(kKeySplashIntroSeen, true)
}

/*
Reset both flags. Reserved for development / "rejouer" debug entry points;
not exposed in the production UI surface.
*/
export async function resetOnboarding(): Promise<void>
{
	await writeBool(kKeyOnboardingDone, false)
	await writeBool(kKeySplashIntroSeen, false)
}
